// Öğretmen paneli ana sayfası: özet, yeni ders ve son dersler.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { DragEvent, FormEvent } from 'react';
import { LuArrowRight, LuFileText, LuPresentation, LuUpload } from 'react-icons/lu';
import { STATUS_LABELS, planOf, type TeacherReport } from '../../shared/plans';
import { LIMITS } from '../../shared/protocol';
import { AppShell, type Me } from '../components/AppShell';
import { api } from '../lib/api';
import { teacherLink } from '../lib/storage';
import { formatDuration, formatWhen } from '../lib/util';

const MIN_PASSWORD_LENGTH = 10;

interface Created {
  id: string;
  teacherKey: string;
}

function uploadLesson(file: File, title: string, onProgress: (r: number) => void): Promise<Created> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/lessons?title=${encodeURIComponent(title)}&file=${encodeURIComponent(file.name)}`);
    xhr.setRequestHeader('Content-Type', 'application/pdf');
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => {
      let body: (Partial<Created> & { error?: string }) | null = null;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        // yok say
      }
      if (xhr.status >= 200 && xhr.status < 300 && body?.id && body.teacherKey) resolve(body as Created);
      else reject(new Error(body?.error ?? `Yükleme başarısız (HTTP ${xhr.status}).`));
    };
    xhr.onerror = () => reject(new Error('Sunucuya ulaşılamadı.'));
    xhr.send(file);
  });
}

export function ChangePasswordForm({ onDone }: { onDone: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/api/auth/password', { method: 'POST', body: { current, next } });
      setCurrent('');
      setNext('');
      setBusy(false);
      onDone();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <label className="field">
        <span>Mevcut şifre</span>
        <input id="password-current" type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
      </label>
      <label className="field">
        <span>Yeni şifre (en az {MIN_PASSWORD_LENGTH} karakter)</span>
        <input id="password-next" type="password" autoComplete="new-password" required minLength={MIN_PASSWORD_LENGTH} value={next} onChange={(e) => setNext(e.target.value)} />
      </label>
      {error && <p className="form-error">{error}</p>}
      <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
        {busy ? 'Kaydediliyor…' : 'Şifreyi kaydet'}
      </button>
    </form>
  );
}

function NewLesson({ maxPdfMb, blocked }: { maxPdfMb: number; blocked: string | null }) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const pick = (f: File | null | undefined) => {
    if (!f) return;
    if (f.type !== 'application/pdf' && !/\.pdf$/i.test(f.name)) return setError('Lütfen bir PDF dosyası seçin.');
    if (f.size > maxPdfMb * 1048576) return setError(`Paketinizde PDF en fazla ${maxPdfMb} MB olabilir.`);
    setError(null);
    setFile(f);
    if (!title.trim()) setTitle(f.name.replace(/\.pdf$/i, ''));
  };

  const create = async (e: FormEvent) => {
    e.preventDefault();
    if (!file || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await uploadLesson(file, title.trim() || file.name, setProgress);
      location.href = teacherLink(r.id, r.teacherKey);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    pick(e.dataTransfer.files?.[0]);
  };

  return (
    <form className="card" onSubmit={create}>
      <div className="card-title">
        <LuPresentation /> <h2>Yeni canlı ders</h2>
      </div>
      <div
        className={`dropzone ${dragging ? 'is-drag' : ''} ${file ? 'has-file' : ''}`}
        onClick={() => input.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && input.current?.click()}
      >
        <input ref={input} type="file" accept="application/pdf,.pdf" hidden onChange={(e) => pick(e.target.files?.[0])} />
        {file ? (
          <>
            <LuFileText className="dz-icon" />
            <strong>{file.name}</strong>
            <span className="muted small">{(file.size / 1048576).toFixed(1)} MB · değiştirmek için tıklayın</span>
          </>
        ) : (
          <>
            <LuUpload className="dz-icon" />
            <strong>PDF’yi buraya bırakın</strong>
            <span className="muted small">ya da seçmek için tıklayın · en fazla {maxPdfMb} MB</span>
          </>
        )}
      </div>
      <label className="field">
        <span>Ders başlığı</span>
        <input id="lesson-title" value={title} maxLength={LIMITS.maxTitleLength} placeholder="ör. AP Calculus — Türev uygulamaları" onChange={(e) => setTitle(e.target.value)} />
      </label>
      {(blocked || error) && <p className="form-error">{blocked ?? error}</p>}
      <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={!file || busy || blocked !== null}>
        {busy ? `Yükleniyor… ${Math.round(progress * 100)}%` : 'Dersi başlat'}
      </button>
    </form>
  );
}

export function Dashboard({ me }: { me: Me }) {
  const { account, user } = me;
  const plan = planOf(account.plan);
  const [report, setReport] = useState<TeacherReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api<TeacherReport>('/api/teacher/report').then(setReport, (err: Error) => setError(err.message));
  }, []);
  useEffect(load, [load]);

  const blocked = !account.entitled
    ? 'Aboneliğiniz aktif değil, yeni ders açılamıyor.'
    : account.lessonCount >= plan.maxLessons
      ? `Paketinizdeki ders sınırına ulaştınız (${plan.maxLessons}). Eski bir dersi silin ya da paketinizi yükseltin.`
      : null;
  const recent = report?.lessons.slice(0, 5) ?? [];

  return (
    <AppShell me={me} page="panel" title={`Hoş geldiniz, ${user.name.split(' ')[0]}`} subtitle="Yeni bir ders başlatın ya da önceki derslerinize dönün.">
      <div className="stats">
        <div className="stat">
          <span className="stat-label">Ders</span>
          <strong className="stat-value">
            {account.lessonCount} <small>/ {plan.maxLessons}</small>
          </strong>
        </div>
        <div className="stat">
          <span className="stat-label">Öğrenci</span>
          <strong className="stat-value">{report ? report.totals.studentCount : '—'}</strong>
        </div>
        <div className="stat">
          <span className="stat-label">Toplam ders süresi</span>
          <strong className="stat-value">{report ? formatDuration(report.totals.teachingMs) : '—'}</strong>
        </div>
        <a className="stat stat-link" href="/profil#paket">
          <span className="stat-label">Paket</span>
          <strong className="stat-value plan-name">{plan.label}</strong>
          <span className={`plan-status ${account.entitled ? 'is-ok' : 'is-off'}`}>{STATUS_LABELS[account.status]}</span>
        </a>
      </div>

      <div className="panel-grid">
        <NewLesson maxPdfMb={plan.maxPdfMb} blocked={blocked} />
        <section className="card">
          <div className="section-head">
            <h2>Son dersler</h2>
            <a href="/derslerim" className="section-link">
              Tümü <LuArrowRight />
            </a>
          </div>
          {error && <p className="form-error">{error}</p>}
          {!report && !error && <p className="muted small">Yükleniyor…</p>}
          {report && recent.length === 0 && <p className="muted small">Henüz ders yok. İlk dersinizi soldan başlatın.</p>}
          <ul className="recent-list">
            {recent.map((l) => (
              <li key={l.id}>
                <a href={`/derslerim/${l.id}`}>
                  <strong>{l.title}</strong>
                  <span className="muted small">
                    {formatWhen(l.lastActivityMs)} · {l.studentCount} öğrenci · {formatDuration(l.durationMs)}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </AppShell>
  );
}
